import { DeleteRounded, EditRounded } from "@mui/icons-material";
import MoreVertRoundedIcon from "@mui/icons-material/MoreVertRounded";

import { Box, Button, Popover } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import useAPI from "../../../api/hooks/useAPI";
import useColors from "../../../api/hooks/useColors";
import { platformUsersKeys } from "../../../api/queryKeys";
import { Toast } from "../../../components/Toast";
import ConfirmationDialog from "../../../components/dialogs/ConfirmationDialog";
import EditPlatformUser from "./EditPlatformUser";
import { getDisplayEmail } from "./utils";

export default function DeleteOrEditMember({ platformUser, titleOptions }) {
  const { deletePlatformUser, trackEvent } = useAPI();
  const colors = useColors();

  const queryClient = useQueryClient();
  const [anchorEl, setAnchorEl] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openEditModel, setOpenEditModel] = useState(false);

  const handleClick = (event) => {
    trackEvent(
      `User clicked on the three dots icon for user ${platformUser.email}`
    );
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    trackEvent(
      `User closed the delete and edit model for user ${platformUser.email}`
    );
    setAnchorEl(null);
  };

  const handleDelete = () => {
    trackEvent(`User clicked on delete for user ${platformUser.email}`);
    setOpenDeleteDialog(true);
    handleClose();
  };

  const handleEdit = () => {
    trackEvent(`User clicked on edit for user ${platformUser.email}`);
    setOpenEditModel(true);
    handleClose();
  };

  const handleEditModelClose = () => {
    setOpenEditModel(false);
    const message = "User close the add user dialog";
    trackEvent(message).then();
  };

  const handleDeleteDialogClose = () => {
    trackEvent(
      `User cancelled delete user ${platformUser.email} in the delete dialog`
    );
    setOpenDeleteDialog(false);
  };

  const handleDeleteConfirm = () => {
    trackEvent(`User confirmed to delete user ${platformUser.email}`);
    setOpenDeleteDialog(false);
    handleDeleteUser(platformUser.email);
  };

  const handleDeleteUser = (email) => {
    deletePlatformUser(email)
      .then(() => {
        Toast.success("User deleted successfully");
        queryClient.invalidateQueries({
          queryKey: platformUsersKeys.all,
        });
      })
      .catch((error) => {
        if (error.response && error.response.status === 403) {
          Toast.error(error.response.data);
        } else {
          Toast.error("An error occurred while deleting the user.");
        }
      });
  };

  return (
    <>
      <div>
        <MoreVertRoundedIcon
          onClick={(event) => handleClick(event)}
          sx={{
            cursor: "pointer",
            fontSize: "20px",
            color: colors.grey_700,
          }}
        />
        <Popover
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleClose}
          anchorOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          slotProps={{
            paper: {
              sx: {
                borderRadius: "9px",
                boxShadow: "0px 2px 4px 0px rgba(0, 0, 0, 0.1)",
              },
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              padding: "3px",
            }}
          >
            <Button
              aria-label="edit"
              onClick={handleEdit}
              startIcon={<EditRounded fontSize="small" />}
              sx={{
                justifyContent: "flex-start",
                padding: "6px 12px",
                textTransform: "none",
                fontSize: "12px",
                borderRadius: "8px",
                "&:hover": {
                  backgroundColor: colors.grey_200,
                },
              }}
            >
              Edit
            </Button>
            <Button
              aria-label="delete"
              onClick={handleDelete}
              startIcon={<DeleteRounded fontSize="small" />}
              sx={{
                justifyContent: "flex-start",
                padding: "6px 12px",
                textTransform: "none",
                fontSize: "12px",
                borderRadius: "8px",
                "&:hover": {
                  backgroundColor: colors.grey_200,
                },
              }}
            >
              Delete
            </Button>
          </Box>
        </Popover>
      </div>
      <ConfirmationDialog
        openDialog={openDeleteDialog}
        handleDialogClose={handleDeleteDialogClose}
        handleConfirm={handleDeleteConfirm}
        dialogContentFirstLine="Are you sure you want to proceed?"
        dialogContentSecondLine={`This action will permanently remove the member from the platform. ${getDisplayEmail(
          platformUser
        )}`}
        confirmationButtonText="Delete"
        destructive={true}
      />
      <EditPlatformUser
        open={openEditModel}
        handleClose={handleEditModelClose}
        platformUser={platformUser}
        titleOptions={titleOptions}
      />
    </>
  );
}
